import { FC } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import MossBrand from '../../features/moss-chat/components/MossBrand'
import { ICONS } from '@/constants/icons'

interface MobileNavProps {
  isOpen: boolean
  onClose: () => void
}

const MobileNav: FC<MobileNavProps> = ({ isOpen, onClose }) => {
  const location = useLocation()
  const isMossUser = useAuthStore((state) => state.isMossUser)

  // 判断当前用户是否需要隐藏工作流和运行日志
  const shouldHideWorkflowAndRunlog = isMossUser()

  const isActive = (path: string) => location.pathname.startsWith(path)

  return (
    <>
      {/* 背景遮罩 */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm z-40 lg:hidden animate-fade-in"
          onClick={onClose}
        />
      )}

      {/* 抽屉菜单 */}
      <aside
        className={`fixed top-0 left-0 h-full w-[280px] bg-page-side shadow-2xl z-50 transform transition-transform duration-300 ease-in-out lg:hidden border-r border-line-subtle ${isOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        {/* Header */}
        <div className="h-[80px] flex items-center justify-between px-6 border-b border-line-subtle">
          <div className="flex items-center">
            <MossBrand size="small" />
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors text-content-muted hover:text-content-primary"
            aria-label="Close Menu"
          >
            <ICONS.UI.CLOSE className="text-lg" />
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="px-4 py-6 space-y-2">
          {/* 工作流 - 当用户名包含 vinka 时隐藏 */}
          {!shouldHideWorkflowAndRunlog && (
            <Link
              to="/app/workflow"
              onClick={onClose}
              className={`group flex items-center px-4 py-3.5 rounded-xl transition-all duration-300 relative overflow-hidden ${isActive('/app/workflow')
                ? 'bg-indigo-600/20 text-white shadow-[0_0_20px_rgba(79,70,229,0.3)] border border-indigo-500/30'
                : 'text-content-muted hover:text-content-primary hover:bg-white/5'
                }`}
            >
              {isActive('/app/workflow') && (
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 to-transparent opacity-50"></div>
              )}
              <ICONS.NAV.WORKFLOW className={`text-xl transition-transform duration-300 ${isActive('/app/workflow') ? 'scale-110 text-indigo-400' : 'group-hover:scale-110'}`} />
              <span className="ml-3 font-medium tracking-wide relative z-10">工作流</span>
            </Link>
          )}

          <Link
            to="/app/moss-chat"
            onClick={onClose}
            className={`group flex items-center px-4 py-3.5 rounded-xl transition-all duration-300 relative overflow-hidden ${isActive('/app/moss-chat')
              ? 'bg-blue-600/20 text-white shadow-[0_0_20px_rgba(37,99,235,0.3)] border border-blue-500/30'
              : 'text-content-muted hover:text-content-primary hover:bg-white/5'
              }`}
          >
            {isActive('/app/moss-chat') && (
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-transparent opacity-50"></div>
            )}
            <ICONS.NAV.AGENT className={`text-xl transition-transform duration-300 ${isActive('/app/moss-chat') ? 'scale-110 text-blue-400' : 'group-hover:scale-110'}`} />
            <span className="ml-3 font-medium tracking-wide relative z-10">智能体</span>
          </Link>

          <Link
            to="/app/skills"
            onClick={onClose}
            className={`group flex items-center px-4 py-3.5 rounded-xl transition-all duration-300 relative overflow-hidden ${isActive('/app/skills')
              ? 'bg-blue-600/10 text-white border border-blue-500/20'
              : 'text-content-muted hover:text-content-primary hover:bg-white/5'
              }`}
          >
            <div className="w-6 flex items-center justify-center">
              <ICONS.FILE.FOLDER className={`text-lg transition-transform duration-300 ${isActive('/app/skills') ? 'scale-110 text-blue-400' : 'group-hover:scale-110'}`} />
            </div>
            <span className="ml-3 font-medium tracking-wide relative z-10">我的技能</span>
          </Link>

          {/* 运行日志 - 当用户名包含 vinka 时隐藏 */}
          {!shouldHideWorkflowAndRunlog && (
            <Link
              to="/app/runlog"
              onClick={onClose}
              className={`group flex items-center px-4 py-3.5 rounded-xl transition-all duration-300 relative overflow-hidden ${isActive('/app/runlog')
                ? 'bg-emerald-600/20 text-white shadow-[0_0_20px_rgba(16,185,129,0.3)] border border-emerald-500/30'
                : 'text-content-muted hover:text-content-primary hover:bg-white/5'
                }`}
            >
              {isActive('/app/runlog') && (
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-transparent opacity-50"></div>
              )}
              <ICONS.NAV.RUNLOG className={`text-xl transition-transform duration-300 ${isActive('/app/runlog') ? 'scale-110 text-emerald-400' : 'group-hover:scale-110'}`} />
              <span className="ml-3 font-medium tracking-wide relative z-10">运行日志</span>
            </Link>
          )}

          <Link
            to="/app/image-chat"
            onClick={onClose}
            className={`group flex items-center px-4 py-3.5 rounded-xl transition-all duration-300 relative overflow-hidden ${isActive('/app/image-chat')
              ? 'bg-pink-600/20 text-white shadow-[0_0_20px_rgba(236,72,153,0.3)] border border-pink-500/30'
              : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
          >
            {isActive('/app/image-chat') && (
              <div className="absolute inset-0 bg-gradient-to-r from-pink-500/10 to-transparent opacity-50"></div>
            )}
            <ICONS.NAV.CANVAS className={`text-xl transition-transform duration-300 ${isActive('/app/image-chat') ? 'scale-110 text-pink-400' : 'group-hover:scale-110'}`} />
            <span className="ml-3 font-medium tracking-wide relative z-10">创意画布</span>
          </Link>
        </nav>
      </aside>
    </>
  )
}

export default MobileNav
